import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import OutputFormatPicker from '../shared/OutputFormatPicker';
import SettingToggle from '../shared/SettingToggle';
import { useCompareUrls } from '../shared/heicDisplay';
import { IconDownload, IconRefresh } from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { recordToolUsage } from '../../../../../services/toolsService';
import { zipImageFiles } from '../../pdf/pdf-to-image/pdfToImageEngine';
import useImageFileEdits from '../../components/image/useImageFileEdits';
import useImagePaste from '../../components/image/useImagePaste';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import ImageGridPreview from '../shared/ImageGridPreview';
import ToolCard from '../../components/common/ToolCard';
import ImagePreviewModal from '../../../../../components/common/ImagePreviewModal';
import {
    DEFAULT_CONVERT_SETTINGS,
    MAX_DIMENSION_OPTIONS,
    OUTPUT_FORMATS,
    canPreserveExif,
    convertImageFile,
    isSupportedConvertImageFile,
} from './convertImageEngine';
import {
    deriveToolStatus,
    downloadBlob,
    getToolStatusText,
} from '../../toolPageUtils';

const MAX_FILE_BYTES = 50 * 1024 * 1024;

const ACCEPT = [
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/gif',
    'image/bmp',
    'image/heic',
    'image/heif',
    'image/avif',
    '.jpg',
    '.jpeg',
    '.png',
    '.webp',
    '.gif',
    '.bmp',
    '.heic',
    '.heif',
    '.avif',
].join(',');

const fileKey = (file) => `${file.name}-${file.size}`;

export default function ConvertImage() {
    const { t } = useTranslation();
    const fileInputRef = useRef(null);

    const [files, setFiles] = useState([]);
    const [format, setFormat] = useState('jpg');
    const [quality, setQuality] = useState(DEFAULT_CONVERT_SETTINGS.quality);
    const [maxDimension, setMaxDimension] = useState(DEFAULT_CONVERT_SETTINGS.maxDimension);
    const [preserveExif, setPreserveExif] = useState(DEFAULT_CONVERT_SETTINGS.preserveExif);
    const [isDragging, setIsDragging] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [progress, setProgress] = useState(null);
    const [previewIndex, setPreviewIndex] = useState(0);
    const [results, setResults] = useState([]);
    const [error, setError] = useState(null);

    const invalidateResults = () => {
        setResults([]);
        setError(null);
        setProgress(null);
        setPreviewIndex(0);
    };

    const addFiles = useCallback((incoming) => {
        const list = Array.from(incoming || []);
        const images = list.filter(isSupportedConvertImageFile);
        if (!images.length) {
            const msg = t('convertImage.errors.invalidType');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }

        let tooLarge = false;
        setFiles((prev) => {
            const next = [...prev];
            for (const file of images) {
                if (file.size > MAX_FILE_BYTES) {
                    tooLarge = true;
                    continue;
                }
                if (next.some((f) => fileKey(f) === fileKey(file))) continue;
                next.push(file);
            }
            return next;
        });

        if (tooLarge) {
            const msg = t('convertImage.errors.tooLarge');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
        } else {
            setError(null);
        }
    }, [t]);

    // Ctrl/Cmd+V anywhere on the page drops a copied picture straight in.
    useImagePaste(addFiles);

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        addFiles(e.dataTransfer.files);
    };

    const removeFile = (index) => {
        setFiles((prev) => prev.filter((_, i) => i !== index));
        invalidateResults();
    };

    // Crop / rotate / duplicate / delete, shared with every other image tool.
    // The edits rewrite the picked file, so the pipeline below is untouched.
    const { actions: fileActions, cropDialog } = useImageFileEdits({
        files,
        onReplace: (index, file) => {
            setFiles((prev) => prev.map((item, i) => (i === index ? file : item)));
            invalidateResults();
        },
        onInsert: (index, file) => {
            setFiles((prev) => {
                const next = [...prev];
                next.splice(index + 1, 0, file);
                return next;
            });
            invalidateResults();
        },
        onRemove: removeFile,
    });

    const handleReset = () => {
        setFiles([]);
        invalidateResults();
    };

    // Files with no result yet — adding pictures to a finished batch queues only
    // the new ones, so nothing already converted is redone.
    const pendingIndexes = useMemo(
        () => files.reduce((acc, _file, i) => (results[i] ? acc : [...acc, i]), []),
        [files, results],
    );
    const allDone = files.length > 0 && pendingIndexes.length === 0;
    const canRun = pendingIndexes.length > 0;

    const handleConvert = async () => {
        if (!canRun || processing) return;

        recordToolUsage('image/convert', { category: 'image' });

        setProcessing(true);
        setError(null);
        const queue = pendingIndexes;

        try {
            for (let k = 0; k < queue.length; k += 1) {
                const i = queue[k];
                setProgress({ fileIndex: k + 1, fileTotal: queue.length });
                const result = await convertImageFile(files[i], format, { quality, maxDimension, preserveExif });

                // Written at the file's own index (results line up with files) and
                // published immediately, so its card stops spinning right away.
                setResults((prev) => {
                    const next = [...prev];
                    next[i] = result;
                    return next;
                });
            }
            notifications.show({ title: t('common.success'), message: t('convertImage.done'), color: 'green' });
        } catch {
            const msg = t('convertImage.errors.failed');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
        } finally {
            setProcessing(false);
            setProgress(null);
        }
    };

    const doneResults = useMemo(() => results.filter(Boolean), [results]);

    const handleDownload = async () => {
        if (!doneResults.length) return;
        if (doneResults.length === 1) {
            downloadBlob(doneResults[0].blob, doneResults[0].name);
            return;
        }
        const zip = await zipImageFiles(doneResults.map((r) => ({ name: r.name, blob: r.blob })));
        downloadBlob(zip, 'converted-images.zip');
    };

    const status = deriveToolStatus({
        hasInput: files.length > 0,
        canRun,
        processing,
        hasResult: doneResults.length > 0,
        hasError: !!error,
    });

    // ── Before/after lightbox ──
    // Object URLs live as long as the results do; the modal only ever gets URLs,
    // never blobs, so they are made once here instead of per render.
    const [lightboxIndex, setLightboxIndex] = useState(null);
    // HEIC originals are decoded for the "before" pane (see shared/heicDisplay).
    const compareUrls = useCompareUrls(results, files);

    const compareItems = useMemo(
        () => results
            .map((r, i) => (r ? {
                id: i,
                fileName: r.name,
                mimeType: r.blob.type || 'image/jpeg',
                aiImage: compareUrls[i]?.after,
                // What the shared modal calls the "before" pane.
                uploadedImage: compareUrls[i]?.before,
            } : null))
            .filter((item) => item?.aiImage),
        [results, compareUrls],
    );

    const openLightbox = (index) => {
        if (!results[index]) return;
        setPreviewIndex(index);
        setLightboxIndex(index);
    };

    const successLabel = doneResults.length > 1
        ? `${t('convertImage.resultsTitle')} (${toBnDigits(doneResults.length)})`
        : t('convertImage.done');

    const processingLabel = progress?.fileTotal > 1
        ? t('convertImage.progressFileOnly', {
            file: toBnDigits(progress.fileIndex),
            files: toBnDigits(progress.fileTotal),
        })
        : t('convertImage.converting');

    const statusText = getToolStatusText(status, t, {
        ready: t('toolPage.statusText.ready'),
        success: t('convertImage.done'),
    });

    // PNG is the only lossless one on offer, so it is the only one the dial
    // cannot move. “Original” keeps whatever each file already is, which may be
    // any of the others.
    const showQuality = format !== 'png';

    return (
        <>
        <ToolPageLayout
            slug="image/convert"
            subtitleKey="convertImage.subtitle"
            status={status}
            statusText={allDone && !processing ? successLabel : statusText}
            onReset={files.length || error ? handleReset : undefined}
            // Before a run the bar runs the tool; once every picture is done it downloads.
            primaryAction={allDone && !processing ? {
                label: doneResults.length > 1 ? t('convertImage.downloadZip') : t('convertImage.download'),
                onClick: handleDownload,
                icon: IconDownload,
            } : {
                label: t('convertImage.convert'),
                onClick: handleConvert,
                disabled: !canRun || processing,
                loading: processing,
            }}
            // Drops the results only — the pictures and settings stay loaded, so another
            // run is one click away. Clearing everything is what রিসেট is for.
            secondaryAction={allDone && !processing ? {
                label: t('convertImage.startOver'),
                onClick: invalidateResults,
                icon: IconRefresh,
            } : undefined}
            showTrust={false}
            settings={(
                <div className="flex flex-col gap-3">
                    {/* Files are picked in the preview panel, so the left column
                        carries settings only — this input is what both the empty
                        state and the grid's "+" tile open. */}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={ACCEPT}
                        multiple
                        className="hidden"
                        onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
                    />

                    {/* The same three the compress tool offers, in the same
                        order and above the format for the same reason: what the
                        picture will be like, then what it will be saved as.
                        Every change drops finished results, since they no longer
                        match the settings. */}
                    <ToolCard title={t('convertImage.settingsTitle')}>
                        <div className="flex flex-col gap-4">
                            {/* PNG is the only lossless format on offer, so it is
                                the only one the dial cannot move. */}
                            {showQuality ? (
                                <label className="block">
                                    <div className="mb-1.5 flex items-center justify-between">
                                        <span className="text-[13.5px] font-semibold text-[#1C1815] dark:text-zinc-100">
                                            {t('convertImage.qualityLabel')}
                                        </span>
                                        <span className="text-[13px] font-semibold tabular-nums text-[#6B635C] dark:text-zinc-400">
                                            {toBnDigits(quality)}%
                                        </span>
                                    </div>
                                    <input
                                        type="range"
                                        min={10}
                                        max={100}
                                        step={1}
                                        value={quality}
                                        disabled={processing}
                                        onChange={(e) => { setQuality(Number(e.target.value)); invalidateResults(); }}
                                        className="w-full accent-amber-500 disabled:opacity-45"
                                    />
                                    <div className="mt-1 text-[11.5px] text-[#A8A099] dark:text-zinc-500">
                                        {t('convertImage.qualityHint')}
                                    </div>
                                </label>
                            ) : null}

                            <label className="block">
                                <span className="mb-1.5 block text-[13.5px] font-semibold text-[#1C1815] dark:text-zinc-100">
                                    {t('convertImage.maxDimensionLabel')}
                                </span>
                                <select
                                    value={maxDimension}
                                    disabled={processing}
                                    onChange={(e) => { setMaxDimension(e.target.value); invalidateResults(); }}
                                    className="h-[42px] w-full rounded-xl border border-[#ECE8E4] bg-white px-3 text-[13.5px] text-[#1C1815] outline-none transition-colors focus:border-amber-400 disabled:opacity-45 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
                                >
                                    {MAX_DIMENSION_OPTIONS.map((opt) => (
                                        <option key={opt.id} value={opt.id}>
                                            {t(`convertImage.maxDimensions.${opt.id}`)}
                                        </option>
                                    ))}
                                </select>
                                <div className="mt-1 text-[11.5px] text-[#A8A099] dark:text-zinc-500">
                                    {t('convertImage.maxDimensionHint')}
                                </div>
                            </label>

                            <SettingToggle
                                label={t('convertImage.preserveExif')}
                                hint={t('convertImage.preserveExifHint')}
                                // EXIF only survives JPEG → JPEG; say so instead of
                                // silently ignoring the switch for a PNG or a WebP.
                                note={preserveExif && files.some((f) => !canPreserveExif(f, format))
                                    ? t('convertImage.preserveExifJpgOnly')
                                    : null}
                                checked={preserveExif}
                                disabled={processing}
                                onChange={(next) => { setPreserveExif(next); invalidateResults(); }}
                            />
                        </div>
                    </ToolCard>

                    <OutputFormatPicker
                        value={format}
                        onChange={(next) => { setFormat(next); invalidateResults(); }}
                        formats={OUTPUT_FORMATS}
                        disabled={processing}
                        transparentOutput
                    />
                </div>
            )}
            preview={(
                <ToolPreviewPanel
                    // Fixed height suits one or two big pictures; a grid of three or more just hugs its rows.
                    tall={files.length <= 2}
                    title={t('convertImage.previewTitle')}
                    status={status}
                    statusLabel={t(`toolPage.status.${status}`)}
                    idleTitle={t('convertImage.previewEmpty')}
                    idleHint={t('convertImage.previewEmptyHint')}
                    processingLabel={processingLabel}
                    progress={progress?.fileTotal
                        ? Math.round((progress.fileIndex / progress.fileTotal) * 100)
                        : undefined}
                    error={error}
                    canvasTone="plain"
                    processingTone="none"
                    showPasteHint
                    showIdle={!files.length && !error}
                    onIdleClick={() => fileInputRef.current?.click()}
                    onIdleDrop={handleDrop}
                    onIdleDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                    onIdleDragLeave={() => setIsDragging(false)}
                    idleDragging={isDragging}
                >
                    {/* Kept mounted while processing: each card spins on its own, so
                        the wait shows the work and finished files can be opened. */}
                    {files.length > 0 && !error ? (
                        <ImageGridPreview
                            // One picture fills the panel, two sit side by side, three or more make a grid.
                            adaptive
                            files={files}
                            results={results}
                            // A conversion can grow the file (PNG from JPG), so no
                            // "saved %" badge — the card shows both sizes instead.
                            variant="preview"
                            actions={processing || doneResults.length ? [] : fileActions}
                            busyIndexes={processing ? pendingIndexes : undefined}
                            activeIndex={doneResults.length ? previewIndex : undefined}
                            onItemClick={doneResults.length ? openLightbox : undefined}
                            onAddMore={processing ? undefined : () => fileInputRef.current?.click()}
                            onAddDrop={processing ? undefined : handleDrop}
                            addLabel={t('convertImage.addMore')}
                        />
                    ) : null}
                </ToolPreviewPanel>
            )}
        />
        {cropDialog}
        <ImagePreviewModal
            opened={lightboxIndex !== null}
            onClose={() => setLightboxIndex(null)}
            images={compareItems}
            initialImage={compareItems.find((item) => item.id === lightboxIndex) ?? null}
            // `adjusted` is the converted image re-rendered with the viewer's
            // brightness/contrast/sharpness edits burnt in; without edits it is
            // null and the untouched result goes out.
            onDownloadImage={(img, adjusted) => {
                const r = results[img?.id];
                if (!r) return;
                downloadBlob(adjusted || r.blob, r.name);
            }}
            initialViewMode="side-by-side"
            useImageTitle
            hideEdit
            hidePrint
            hideShare
            hideCustomDownload
            hideDelete
        />
        </>
    );
}
